import React, { useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { SupplierGroup, listingKeyOf } from './listingGroups';
import ListingRows, { ShortlistTick } from './ListingRows';
import ContactEmailBadge from '../suppliers/ContactEmailBadge';
import { MetricTone, formatLead, leadTone, priceTone, rangeSummary } from './discoveryInsights';
import TrustChip from './TrustChip';
import PartNumberChip from './PartNumberChip';
import SupplierFlags from './SupplierFlags';
import SupplierDetailBlocks from './SupplierDetailBlocks';
import SpecMismatchNote from './SpecMismatchNote';

/**
 * "N other suppliers" accordion (v2.1 mock): collapsed summary shows the
 * price / lead / trust spans across the group; open, each row lists unit,
 * lead, trust and a shortlist toggle, and clicking a row expands the same
 * detail blocks the pick cards use — one data path for every supplier.
 * One row per supplier: a supplier with several matching products lists them
 * under its row, each with its own tick.
 */
interface OtherSuppliersAccordionProps {
  groups: SupplierGroup[];
  /** False when nothing above claims a ranking — then this is the whole list. */
  hasPicksAbove: boolean;
  tickedKeys: readonly string[];
  onToggleListing: (listingKey: string) => void;
  /** Short part name for the button ("Shortlist for Brushless motor"). */
  partLabel?: string;
  quantity?: number;
  targetPrice?: number;
  leadWindowWeeks?: number;
  requiredCompliance?: string[];
  onAddToFirstMessage?: (supplierName: string, note: string) => void;
}

const TONE_TEXT: Record<MetricTone, string> = {
  success: 'text-success-ink',
  warn: 'text-warn-ink',
  ink: 'text-ink',
};

const ROW_GRID =
  'grid grid-cols-[minmax(0,1.4fr)_64px_58px_76px_auto] items-center gap-s4 px-[14px] ' +
  'max-md:flex max-md:flex-wrap max-md:gap-x-s4 max-md:gap-y-s2';

export const OtherSuppliersAccordion: React.FC<OtherSuppliersAccordionProps> = ({
  groups,
  hasPicksAbove,
  tickedKeys,
  onToggleListing,
  partLabel,
  quantity,
  targetPrice,
  leadWindowWeeks,
  requiredCompliance,
  onAddToFirstMessage,
}) => {
  // The whole list lands here when research produced no ranking — then the
  // group opens by default so nothing is hidden behind a fold.
  const [open, setOpen] = useState(!hasPicksAbove);
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const summary = rangeSummary(groups.map((g) => g.best));

  return (
    <div className="shrink-0 overflow-hidden rounded-card border border-line bg-surface">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center gap-s3 px-[14px] py-[10px] text-left font-sans"
      >
        <ChevronRight className={`h-[14px] w-[14px] shrink-0 text-ink-muted transition-transform ${open ? 'rotate-90' : ''}`} />
        <span className="text-[12.5px] font-semibold text-ink">
          {groups.length} {hasPicksAbove ? 'other ' : ''}supplier{groups.length === 1 ? '' : 's'}
        </span>
        {summary && <span className="text-meta text-ink-muted">{summary}</span>}
      </button>

      {open && (
        <div className="border-t border-line-divider-soft">
          <div className={`${ROW_GRID} border-b border-line-divider-soft py-[7px] text-table-header uppercase text-ink-muted max-md:hidden`}>
            <span>Supplier</span>
            <span>Unit</span>
            <span>Lead</span>
            <span>Trust</span>
            <span />
          </div>
          {groups.map((group) => {
            const s = group.best;
            const multi = group.listings.length > 1;
            const expanded = expandedKey === group.key;
            const toggleExpanded = () => setExpandedKey(expanded ? null : group.key);
            return (
              <div key={group.key} className="border-b border-line-divider last:border-b-0">
                <div
                  role="button"
                  tabIndex={0}
                  aria-expanded={expanded}
                  onClick={toggleExpanded}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggleExpanded();
                    }
                  }}
                  className={`${ROW_GRID} cursor-pointer py-[9px] hover:bg-surface-hover`}
                >
                  <div className="flex min-w-0 items-center gap-[9px] max-md:basis-full">
                    <div className="min-w-0">
                      <div className="flex items-center gap-s2">
                        <span className="truncate text-[12.5px] font-semibold text-ink">{s.name}</span>
                        <PartNumberChip check={s.partNumberCheck} />
                        <ContactEmailBadge
                          variant="icon"
                          hasContactEmail={s.hasContactEmail}
                          findingContact={s.findingContact}
                          noEmailTitle="No contact email found in research"
                        />
                      </div>
                      <SupplierFlags supplier={s} className="my-[2px]" />
                      {s.reason && (
                        <div
                          className="truncate text-[10.5px] text-ink-muted max-md:overflow-visible max-md:whitespace-normal"
                          title={s.reason}
                        >
                          {s.reason}
                        </div>
                      )}
                      <SpecMismatchNote mismatches={s.specMismatches ?? []} className="mt-[2px]" />
                    </div>
                  </div>
                  <span className={`font-mono text-[12.5px] font-semibold ${TONE_TEXT[priceTone(s.quotedPrice, targetPrice)]}`}>
                    {s.quotedPrice != null ? `${s.currencySymbol}${s.quotedPrice.toLocaleString()}` : '—'}
                  </span>
                  <span className={`font-mono text-[12.5px] ${TONE_TEXT[leadTone(s.leadTimeWeeks, leadWindowWeeks)]}`}>
                    {s.leadTimeWeeks != null ? formatLead(s.leadTimeWeeks) : '—'}
                  </span>
                  <TrustChip score={s.trustScore} trust={s.trust} className="justify-self-start" />
                  {multi ? (
                    <span className="justify-self-start whitespace-nowrap text-meta font-semibold text-ink-secondary max-md:ml-auto">
                      {group.listings.length} products
                    </span>
                  ) : (
                    <span className="max-md:ml-auto">
                      <ShortlistTick
                        ticked={tickedKeys.includes(listingKeyOf(s))}
                        onToggle={() => onToggleListing(listingKeyOf(s))}
                        label={partLabel ? `Shortlist for ${partLabel}` : undefined}
                      />
                    </span>
                  )}
                </div>
                {multi && (
                  <div className="px-[14px] pb-[6px] pl-[28px] max-md:pl-[14px]">
                    <ListingRows listings={group.listings} tickedKeys={tickedKeys} onToggle={onToggleListing} />
                  </div>
                )}
                {expanded && (
                  <div className="px-[14px] pb-[12px]">
                    <SupplierDetailBlocks
                      supplier={s}
                      quantity={quantity}
                      requiredCompliance={requiredCompliance}
                      onAddToFirstMessage={onAddToFirstMessage && ((note) => onAddToFirstMessage(s.name, note))}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default OtherSuppliersAccordion;
